import { EventEmitter, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, map, Observable } from 'rxjs';

import { PageContextFacade, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  BulkActionsProps,
  CanvasElementEvent,
  RowActionsProps,
  RowClickActionProps,
} from '@blaze-runtime/engine-domain';
import {
  ComparisonOperator,
  LogicalPredicateNode,
  LogicalType,
  PredicateType,
} from '@blaze-shared/api-contracts-domain';
import {
  ContainerActionConfig,
  DataBindingSourceInfo,
  ElementRedirect,
  ElementRedirectTargetType,
  findWorkflowGetSourceNode,
  FormContext,
  ItemContainerActionsIds,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_ADD_CONVERSATION,
  ITEMS_CONTAINER_ACTION_DELETE,
  ITEMS_CONTAINER_ACTION_DELETE_CONVERSATION,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION,
  ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION,
  ITEMS_CONTAINER_ACTION_LINK_CLICK,
  ITEMS_CONTAINER_ACTION_ROW_CLICK,
  ITEMS_CONTAINER_ACTION_SEND_MESSAGE,
  ITEMS_CONTAINER_ACTION_VIEW,
  ITEMS_CONTAINER_ACTION_VIEW_DIALOG,
  ITEMS_CONTAINER_BULK_ACTION_DELETE,
  ITEMS_CONTAINER_BULK_ACTION_EDIT,
  ITEMS_CONTAINER_BULK_ACTION_EXPORT,
  RowClickAction,
  RowClickActionDef,
} from '@blaze-shared/elements-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { PageContext } from '@blaze-shared/projects-domain';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { findColumnIndex } from '@blaze-shared/schema-domain';
import {
  DatasetColumn,
  DatasetRow,
  type ElementSchema,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import { isNullishOrEmptyString } from '@blaze-shared/shared-util';
import { WorkflowPhase } from '@blaze-shared/workflow-domain';

import { setStyleSheetForAction } from '../+state/elements/elements.actions';
import { EngineState } from '../+state/engine/engine.model';
import { RuntimeFacade } from '../+state/runtime/runtime.facade';
import { isInputDataPackage } from '../canvas-elements/io-data/io-data.models';

import { DialogOverlayService } from './dialog/dialog-overlay.service';
import { ProjectNavigateService } from './project-navigate.service';
import { RowGenerationService } from './row-generation.service';

export interface ListLikeActionArgs {
  action: { id?: string; action?: ContainerActionConfig };
  dataRow?: any /* row-action data*/;
  dataRowId?: string /* row-action rowId */;
  dataRowIds?: string[] | null /* bulk-action rowIds */;
  dataRowSchema?: ElementSchema /* schema of the row-action data */;
  dataSource: DataBindingSourceInfo;
  formContext?: FormContext /* for dialog actions */;
  isBulkAction?: boolean;
  output?: EventEmitter<CanvasElementEvent>;
  props: RowActionsProps | BulkActionsProps | undefined;
}

interface PreparePageContextArgs {
  data: any;
  dataRowSchema: ElementSchema | undefined;
  dataRowId: string | undefined;
  formContext: FormContext | undefined;
  targetId: string;
  targetType: ElementRedirectTargetType | undefined;
  isEmptyishRow: boolean;
}

export interface ListLikeActionResult {
  targetType: ElementRedirectTargetType | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class ContainerActionsService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly dialogOverlayService = inject(DialogOverlayService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly projectNavigateService = inject(ProjectNavigateService);
  private readonly rowGenerationService = inject(RowGenerationService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<EngineState>);

  async handleListlikeAction(args: ListLikeActionArgs): Promise<ListLikeActionResult | undefined> {
    const {
      action,
      dataRow,
      dataRowId,
      dataRowIds,
      dataRowSchema,
      dataSource,
      formContext,
      isBulkAction,
      output,
      props,
    } = args;

    let targetId: string | undefined = undefined;
    let targetType: ElementRedirectTargetType | undefined = undefined;
    let eventType: RuntimeEventType = 'action';
    let dialogClosedHandler: ((row: any) => void) | undefined = undefined;
    const rowActions = (props as RowActionsProps & RowClickActionProps)?.content;
    const bulkActions = (props as BulkActionsProps)?.content;

    try {
      switch (action.id) {
        // row actions
        case ITEMS_CONTAINER_ACTION_ADD:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Add dialog',
            rowActions?.itemAdd,
          );
          dialogClosedHandler = (row: any): void => {
            this.datasetRowsService.insert({ datasetId: dataSource.id, row }).subscribe();
          };
          break;
        case ITEMS_CONTAINER_ACTION_VIEW:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Details view',
            rowActions?.itemView,
            undefined,
            rowActions?.rowClick,
          );
          break;
        case ITEMS_CONTAINER_ACTION_VIEW_DIALOG:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Details view dialog',
            rowActions?.itemViewDialog,
            undefined,
            rowActions?.rowClick,
          );
          break;
        case ITEMS_CONTAINER_ACTION_EDIT:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Edit dialog',
            rowActions?.itemEdit,
            undefined,
            rowActions?.rowClick,
          );
          dialogClosedHandler = (value: any): void => {
            this.datasetRowsService
              .update(dataSource.id, { value, queryNode: this.getIdEqQuery(dataSource.id, value) })
              .subscribe();
          };
          break;
        case ITEMS_CONTAINER_ACTION_DELETE:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Delete workflow',
            rowActions?.itemDelete,
            'Workflow',
          );
          break;

        // table row click
        case ITEMS_CONTAINER_ACTION_ROW_CLICK: {
          const rowClick = (rowActions as RowClickAction)?.rowClick;
          const clickActionId = rowClick?.action;
          const clickAction = clickActionId && rowClick?.targetId[clickActionId];
          const clickTargetAction =
            typeof clickAction === 'string'
              ? { enabled: true, targetId: clickAction }
              : clickAction;
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Row click action',
            clickTargetAction,
            typeof clickAction === 'string' ? 'Workflow' : undefined,
          );
          break;
        }
        // table link value click
        case ITEMS_CONTAINER_ACTION_LINK_CLICK: {
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Row click action',
            action as ElementRedirect,
          );
          break;
        }

        // bulk actions
        case ITEMS_CONTAINER_BULK_ACTION_EXPORT:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Bulk export workflow',
            bulkActions?.bulkExport,
            'Workflow',
          );
          break;
        case ITEMS_CONTAINER_BULK_ACTION_DELETE:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Bulk delete workflow',
            bulkActions?.bulkDelete,
            'Workflow',
          );
          break;
        case ITEMS_CONTAINER_BULK_ACTION_EDIT:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Bulk edit workflow',
            bulkActions?.bulkEdit,
            'Workflow',
          );
          break;
        case ITEMS_CONTAINER_ACTION_SEND_MESSAGE:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Message sent workflow',
            action as ElementRedirect,
            'Workflow',
          );
          eventType = 'action-chat-message-sent';
          break;
        case ITEMS_CONTAINER_ACTION_ADD_CONVERSATION:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Conversation added workflow',
            action as ElementRedirect,
            'Workflow',
          );
          eventType = 'action-chat-conversation-added';
          break;
        case ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Conversation updated workflow',
            action as ElementRedirect,
            'Workflow',
          );
          eventType = 'action-chat-conversation-edited';
          break;
        case ITEMS_CONTAINER_ACTION_DELETE_CONVERSATION:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Conversation deleted workflow',
            action as ElementRedirect,
            'Workflow',
          );
          eventType = 'action-chat-conversation-deleted';
          break;
        case ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION:
          [targetId, targetType] = await this.getAndValidateActionParams(
            'Conversation left workflow',
            action as ElementRedirect,
            'Workflow',
          );
          eventType = 'action-chat-conversation-left';
          break;

        // custom row/bulk actions
        default:
          if (action.action?.workflowId) {
            targetId = action.action.workflowId;
            targetType = 'Workflow';
          }
          break;
      }
    } catch {
      return;
    }

    if (!targetId) {
      return;
    }

    // Initialize the data
    let data = isInputDataPackage(dataRow) ? dataRow.data : dataRow;
    let isEmptyishRow = false;

    // For add action, apply empty row initial values
    if (action.id === ITEMS_CONTAINER_ACTION_ADD) {
      const emptyRow = this.rowGenerationService.getNewRow(dataSource);

      data = { ...emptyRow, ...dataRow };
      isEmptyishRow = true;
    }

    // Prepare page context data when applicable
    const pageContext = await this.preparePageContext({
      data,
      dataRowSchema,
      dataRowId,
      formContext,
      targetId,
      targetType,
      isEmptyishRow,
    });

    switch (targetType) {
      case 'Dialog': {
        this.dialogOverlayService.openDialog(targetId, pageContext).subscribe((result) => {
          if (dialogClosedHandler && result.payload) {
            dialogClosedHandler(result.payload);
          }
        });
        break;
      }
      case 'Drawer': {
        this.dialogOverlayService.openDialog(targetId, pageContext, true).subscribe((result) => {
          if (dialogClosedHandler && result.payload) {
            dialogClosedHandler(result.payload);
          }
        });
        break;
      }
      case 'Page': {
        this.projectNavigateService.navigateToPage({
          targetPageId: targetId,
          pageContext,
          openInNewTab: false,
        });
        break;
      }
      case 'Workflow': {
        const inputData = !isBulkAction ? data : dataRowIds;
        this.triggerWorkflow(
          output,
          inputData,
          dataRowSchema,
          targetId,
          dataSource.id,
          isBulkAction,
          eventType,
        );
        break;
      }
    }

    return { targetType };
  }

  handleSchedulerAction(
    dataRow: any,
    dataRowSchema: ElementSchema | undefined,
    action: ContainerActionConfig | undefined,
    output?: EventEmitter<CanvasElementEvent>,
  ): void {
    if (action?.workflowId) {
      this.triggerWorkflow(output, dataRow, dataRowSchema, action?.workflowId);
    }
  }

  isAddingRowsAllowed(dataSource?: DataBindingSourceInfo): boolean | Observable<boolean> {
    if (!dataSource) {
      return false;
    }
    if (dataSource.sourceType === 'dataset') {
      return true;
    }

    return this.store
      .select(runtimeSelectors.selectWorkflowsStatusById(dataSource.id))
      .pipe(map((status) => !status?.phase || status?.phase === WorkflowPhase.Completed));
  }

  getEmptyRow(dataSource?: DataBindingSourceInfo): DatasetRow {
    return this.rowGenerationService.getNewRow(dataSource, { nullIdColumn: true });
  }

  handleRowUpdate(
    datasetId: string,
    dataRowId: string,
    dataRow: any,
    successMsg?: string,
    errorMsg?: string,
  ): Observable<DatasetRow> {
    return this.datasetRowsService.update(
      datasetId,
      { value: dataRow, rowId: dataRowId },
      { successMsg, errorMsg },
    );
  }

  changeActionStyleSheet(
    elementId: string,
    actionId: ItemContainerActionsIds,
    styleSheetId?: string | null,
    styleSheetName?: string | null,
  ): void {
    this.store.dispatch(
      setStyleSheetForAction({ elementId, actionId, styleSheetId, styleSheetName }),
    );
  }
  triggerWorkflow(
    output: EventEmitter<CanvasElementEvent> | undefined,
    data: unknown,
    schema: ElementSchema | undefined,
    workflowId: string,
    dataSourceId?: string,
    isBulkAction?: boolean,
    eventType: RuntimeEventType = 'action',
  ): void {
    output?.emit({
      eventType,
      data: { data, workflowId, isBulkAction, dataSourceId },
      schema,
      output: 'events',
      isInitialData: false,
    });
  }
  private async preparePageContext({
    data,
    dataRowSchema,
    dataRowId,
    formContext,
    targetId,
    targetType,
    isEmptyishRow,
  }: PreparePageContextArgs): Promise<PageContext | undefined> {
    const targetSupportsPageContext =
      targetType === 'Dialog' || targetType === 'Drawer' || targetType === 'Page';

    // Stop if data missing, or target does not support page context
    if (!data || !targetSupportsPageContext) {
      return undefined;
    }

    // If data is present, so should be the schema
    if (!dataRowSchema) {
      throw new Error(`Data row schema is missing.`);
    }

    const sourceWorkflowId = dataRowSchema.workflowId;
    let updatedSchema = dataRowSchema;
    // Check if the data comes from a workflow and the dataRowId is provided
    if (sourceWorkflowId && !isNullishOrEmptyString(dataRowId)) {
      const { dataRow: newData, columns: newColumns } = await this.appendFullRowData(
        data,
        dataRowSchema.columns,
        dataRowId,
        sourceWorkflowId,
      );
      data = newData;
      updatedSchema = { ...dataRowSchema, columns: newColumns };
    }

    return this.pageContextFacade.generatePageContext({
      data,
      schema: updatedSchema,
      formContext,
      pageId: targetId,
      isEmptyishRow,
    });
  }

  private getIdEqQuery(dataSourceId: string, dataRow: any): LogicalPredicateNode {
    if (!this.schemaService.isDataset(dataSourceId)) {
      throw new Error(`Datasource is not a dataset.`);
    }
    const idCol = this.schemaService.getIdColumnName(dataSourceId);
    return {
      nodeType: PredicateType.Logical,
      logicalType: LogicalType.And,
      children: [
        {
          names: [idCol],
          op: ComparisonOperator.Eq,
          value: dataRow[idCol],
          nodeType: PredicateType.Comparison,
        },
      ],
    };
  }

  private async getAndValidateActionParams(
    actionName: string,
    target: ElementRedirect | undefined,
    targetType?: ElementRedirectTargetType,
    rowClick?: RowClickActionDef,
  ): Promise<[string | undefined, ElementRedirectTargetType | undefined]> {
    if (!target?.enabled) {
      if (rowClick?.enabled) {
        const rowClickAction = rowClick.action;
        const rowActionTarget = rowClickAction ? rowClick.targetId[rowClickAction] : undefined;
        if (
          (rowClickAction === ITEMS_CONTAINER_ACTION_EDIT ||
            rowClickAction === ITEMS_CONTAINER_ACTION_VIEW ||
            rowClickAction === ITEMS_CONTAINER_ACTION_VIEW_DIALOG) &&
          rowActionTarget &&
          typeof rowActionTarget !== 'string'
        ) {
          target = {
            enabled: true,
            targetId: rowActionTarget.targetId,
            targetType: 'Dialog',
          };
        } else if (
          rowClickAction === ITEMS_CONTAINER_ACTION_ROW_CLICK &&
          typeof rowActionTarget === 'string'
        ) {
          target = {
            enabled: true,
            targetId: rowActionTarget,
            targetType: 'Dialog',
          };
        }
      }
      if (!target) {
        throw new Error(`Action failed. '${actionName}' not configured.`);
      }
    }

    if (targetType === 'Workflow' || target?.targetType === 'Workflow') {
      return [target.targetId, 'Workflow'];
    }

    if (target.targetId) {
      const runtimePage = await firstValueFrom(
        this.store.select(runtimeSelectors.selectPageById(target.targetId)),
      );
      const pageType = runtimePage?.pageType;
      targetType =
        pageType === 'Page' || pageType === 'Dialog' || pageType === 'Drawer'
          ? pageType
          : undefined;
    }
    return [target.targetId, targetType ?? target.targetType];
  }

  /**
   * If a source node for the workflow limits which columns are returned from the server (BL-7332), it creates a
   * full inputData object that contains full row information. In any other case, it returns the input dataRow and dataRowColumns.
   * @param dataRow - Original data row (possibly partial). In case when a full data object is created, it merges
   *   original data into that object so that data outputed from a workflow is not lost (for example from lookup nodes).
   * @param dataRowColumns - Columns from the dataRow's schema
   * @param dataRowId - ID of the row to fetch.
   * @param workflowId - Workflow ID of the original. Helps us determine whether full row data should be created.
   */
  private async appendFullRowData(
    dataRow: DatasetRow,
    dataRowColumns: DatasetColumn[],
    dataRowId: string,
    workflowId: string,
  ): Promise<{ dataRow: DatasetRow; columns: DatasetColumn[] }> {
    const wf = this.runtimeFacade.workflows.find(({ id }) => id && id === workflowId);
    const getSourceNode = findWorkflowGetSourceNode(wf);

    if (!getSourceNode?.datasetId || !getSourceNode?.projectColumns?.length) {
      return { dataRow, columns: dataRowColumns };
    }

    // Source get node limits which columns are returned from the server. As such, the input data does not
    // contain all the necessary information to open an edit dialog or details view. We fetch the entire row
    // and merge it with the current input data.
    const fullRowData = await firstValueFrom(
      this.datasetRowsService.getRowById(getSourceNode.datasetId, dataRowId),
    );
    const fullRowSchema = await firstValueFrom(
      this.schemaService.obtainSchema(getSourceNode.datasetId),
    );

    if (!fullRowData?.[0] || !fullRowSchema) {
      throw new Error(`Full row data or schema is missing.`);
    }

    const newDataRow = { ...fullRowData[0], ...dataRow };
    const newColumns = fullRowSchema.columns.reduce<DatasetColumn[]>(
      (acc, column) => {
        const existingColumnIndex = findColumnIndex(acc, column);
        if (existingColumnIndex === -1) {
          acc.push(column);
        } else {
          // the names should never be different
          this.validateNamesAreEqual(acc[existingColumnIndex].name, column.name);
          acc[existingColumnIndex] = column;
        }
        return acc;
      },
      [...dataRowColumns],
    );

    return {
      dataRow: newDataRow,
      columns: newColumns,
    };
  }

  private validateNamesAreEqual(existingColumnName: string, newColumnName: string): void {
    if (existingColumnName !== newColumnName) {
      sentryLog(
        `[${ContainerActionsService.name}.${this.validateNamesAreEqual.name}]: got different columns names with the same id. Expected "${existingColumnName}", got "${newColumnName}"`,
        'warning',
      );
    }
  }
}
